export const formBuilderPaths = {
  '/form-steps': {
    post: {
      summary: 'Create a form step',
      operationId: 'createFormStep',
      tags: ['Form Builder'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                document_form_id: { type: 'integer', description: 'ID of the document form' },
                step_title: { type: 'string', description: 'Title of the step' },
                next_step_label: { type: 'string', description: 'Label for the next step button' }
              },
              required: ['document_form_id']
            }
          }
        }
      },
      responses: {
        '201': {
          description: 'Created form step',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  id: { type: 'integer' },
                  document_form_id: { type: 'integer' },
                  step_order: { type: 'integer' },
                  step_title: { type: 'string' },
                  next_step_label: { type: 'string' },
                  created_at: { type: 'string', format: 'date-time' },
                  updated_at: { type: 'string', format: 'date-time' }
                }
              }
            }
          }
        },
        '400': {
          description: 'Error response',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/form-steps/{id}': {
    parameters: [
      {
        name: 'id',
        in: 'path',
        required: true,
        schema: { type: 'integer' }
      }
    ],
    patch: {
      summary: 'Update a form step',
      operationId: 'updateFormStep',
      tags: ['Form Builder'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                step_title: { type: 'string' },
                step_order: { type: 'integer' },
                next_step_label: { type: 'string' }
              }
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'Updated form step',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  id: { type: 'integer' },
                  document_form_id: { type: 'integer' },
                  step_order: { type: 'integer' },
                  step_title: { type: 'string' },
                  next_step_label: { type: 'string' },
                  created_at: { type: 'string', format: 'date-time' },
                  updated_at: { type: 'string', format: 'date-time' }
                }
              }
            }
          }
        },
        '404': {
          description: 'Step not found',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    },
    delete: {
      summary: 'Delete a form step',
      operationId: 'deleteFormStep',
      tags: ['Form Builder'],
      responses: {
        '204': { description: 'Step deleted successfully' },
        '404': {
          description: 'Step not found',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/form-steps/reorder': {
    patch: {
      summary: 'Reorder form steps',
      operationId: 'reorderFormSteps',
      tags: ['Form Builder'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                document_form_id: { type: 'integer' },
                steps: { 
                  type: 'array',
                  items: { type: 'integer' },
                  description: 'Array of step IDs in the new order'
                }
              },
              required: ['document_form_id', 'steps']
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'Steps reordered successfully',
          content: {
            'application/json': {
              schema: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'integer' },
                    document_form_id: { type: 'integer' },
                    step_order: { type: 'integer' },
                    step_title: { type: 'string' },
                    next_step_label: { type: 'string' },
                    created_at: { type: 'string', format: 'date-time' },
                    updated_at: { type: 'string', format: 'date-time' }
                  }
                }
              }
            }
          }
        },
        '400': {
          description: 'Error response',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/form-sections': {
    post: {
      summary: 'Create a form section',
      operationId: 'createFormSection',
      tags: ['Form Builder'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                form_step_id: { type: 'integer' },
                section_title: { type: 'string' },
                section_type: { type: 'string' },
                multiple_elements: { type: 'boolean' },
                min_quantity: { 
                  type: 'integer',
                  description: 'Minimum quantity of elements required when multiple_elements is true',
                  nullable: true
                }
              },
              required: ['form_step_id']
            }
          }
        }
      },
      responses: {
        '201': {
          description: 'Created form section',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  id: { type: 'integer' },
                  form_step_id: { type: 'integer' },
                  section_order: { type: 'integer' },
                  section_title: { type: 'string' },
                  section_type: { type: 'string' },
                  multiple_elements: { type: 'boolean' },
                  min_quantity: { type: 'integer', nullable: true },
                  created_at: { type: 'string', format: 'date-time' },
                  updated_at: { type: 'string', format: 'date-time' }
                }
              }
            }
          }
        },
        '400': {
          description: 'Error response',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/form-sections/{id}': {
    parameters: [
      {
        name: 'id',
        in: 'path',
        required: true,
        schema: { type: 'integer' }
      }
    ],
    patch: {
      summary: 'Update a form section',
      operationId: 'updateFormSection',
      tags: ['Form Builder'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                section_title: { type: 'string' },
                section_order: { type: 'integer' },
                section_type: { type: 'string' },
                multiple_elements: { type: 'boolean' },
                min_quantity: { 
                  type: 'integer', 
                  description: 'Minimum quantity of elements required when multiple_elements is true',
                  nullable: true
                }
              }
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'Updated form section',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  id: { type: 'integer' },
                  form_step_id: { type: 'integer' },
                  section_order: { type: 'integer' },
                  section_title: { type: 'string' },
                  section_type: { type: 'string' },
                  multiple_elements: { type: 'boolean' },
                  min_quantity: { type: 'integer', nullable: true },
                  created_at: { type: 'string', format: 'date-time' },
                  updated_at: { type: 'string', format: 'date-time' }
                }
              }
            }
          }
        },
        '404': {
          description: 'Section not found',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    },
    delete: {
      summary: 'Delete a form section',
      operationId: 'deleteFormSection',
      tags: ['Form Builder'],
      responses: {
        '204': { description: 'Section deleted successfully' },
        '404': {
          description: 'Section not found',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/form-fields': {
    post: {
      summary: 'Create a form field',
      operationId: 'createFormField',
      tags: ['Form Builder'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                form_section_id: { type: 'integer' },
                field_type: { type: 'string' },
                label: { type: 'string' },
                name: { type: 'string' },
                filled_by: { type: 'string' },
                is_required: { type: 'boolean' },
                content: { type: 'string' },
                options: { 
                  type: 'array',
                  items: { 
                    type: 'object',
                    properties: {
                      label: { type: 'string' },
                      value: { type: 'string' }
                    }
                  }
                },
                validation_rules: { type: 'object' },
                default_value: { type: 'string' }
              },
              required: ['form_section_id', 'field_type']
            }
          }
        }
      },
      responses: {
        '201': {
          description: 'Created form field',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  id: { type: 'integer' },
                  form_section_id: { type: 'integer' },
                  field_order: { type: 'integer' },
                  field_type: { type: 'string' },
                  label: { type: 'string' },
                  name: { type: 'string' },
                  content: { type: 'string' },
                  filled_by: { type: 'string' },
                  default_value: { type: 'string' },
                  validation_rules: { type: 'object' },
                  is_required: { type: 'boolean' },
                  created_at: { type: 'string', format: 'date-time' },
                  updated_at: { type: 'string', format: 'date-time' }
                }
              }
            }
          }
        },
        '400': {
          description: 'Error response',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  },
  '/form-fields/{id}': {
    parameters: [
      {
        name: 'id',
        in: 'path',
        required: true,
        schema: { type: 'integer' }
      }
    ],
    patch: {
      summary: 'Update a form field',
      operationId: 'updateFormField',
      tags: ['Form Builder'],
      requestBody: {
        required: true,
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                field_type: { type: 'string' },
                label: { type: 'string' },
                name: { type: 'string' },
                field_order: { type: 'integer' },
                filled_by: { type: 'string' },
                is_required: { type: 'boolean' },
                content: { type: 'string' },
                options: { 
                  type: 'array',
                  items: { 
                    type: 'object',
                    properties: {
                      label: { type: 'string' },
                      value: { type: 'string' }
                    }
                  }
                },
                validation_rules: { type: 'object' },
                default_value: { type: 'string' }
              }
            }
          }
        }
      },
      responses: {
        '200': {
          description: 'Updated form field',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                properties: {
                  id: { type: 'integer' },
                  form_section_id: { type: 'integer' },
                  field_order: { type: 'integer' },
                  field_type: { type: 'string' },
                  label: { type: 'string' },
                  name: { type: 'string' },
                  content: { type: 'string' },
                  filled_by: { type: 'string' },
                  default_value: { type: 'string' },
                  validation_rules: { type: 'object' },
                  is_required: { type: 'boolean' },
                  created_at: { type: 'string', format: 'date-time' },
                  updated_at: { type: 'string', format: 'date-time' }
                }
              }
            }
          }
        },
        '404': {
          description: 'Field not found',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    },
    delete: {
      summary: 'Delete a form field',
      operationId: 'deleteFormField',
      tags: ['Form Builder'],
      responses: {
        '204': { description: 'Field deleted successfully' },
        '404': {
          description: 'Field not found',
          content: {
            'application/json': {
              schema: { $ref: '#/components/schemas/ErrorResponse' }
            }
          }
        }
      }
    }
  }
};
